import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, CheckCircleIcon, EyeIcon } from '@heroicons/react/24/outline';
import EntryPointSelector from '../components/builder/EntryPointSelector';
import ConversationUI from '../components/builder/ConversationUI';
import PasteTransformFlow from '../components/builder/PasteTransformFlow';
import { ResumePreview, TemplateSelector, MobilePreviewDrawer } from '../components/preview';
import { Card, Button, Loading } from '../components/common';
import * as conversationService from '../services/conversationService';
import { getConversationPreview } from '../services/templateService';
import { useAuth } from '../context/AuthContext';

const ResumeBuilderPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stage, setStage] = useState('select'); // select, paste, conversation, review
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Preview state
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [hasWatermark, setHasWatermark] = useState(true);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);

  const isPro = user?.subscription?.status === 'active';

  // Check for existing active session on mount
  useEffect(() => {
    const checkActiveSession = async () => {
      try {
        const response = await conversationService.getActiveConversation();
        if (response.success && response.data) {
          setSession(response.data);
          setStage('conversation');
        }
      } catch (error) {
        console.error('Error checking active session:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    checkActiveSession();
  }, []);

  // Fetch preview when session content or template changes
  const fetchPreview = useCallback(async () => {
    if (!session?.id || !session.extractedContent?.contactInfo?.fullName) {
      setPreviewHtml('');
      return;
    }

    setPreviewLoading(true);
    try {
      const response = await getConversationPreview(session.id, selectedTemplate);
      if (response.success) {
        setPreviewHtml(response.data.html);
        setHasWatermark(response.data.hasWatermark);
      }
    } catch (error) {
      console.error('Error fetching preview:', error);
    } finally {
      setPreviewLoading(false);
    }
  }, [session?.id, session?.extractedContent, selectedTemplate]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  const handleEntryPointSelect = async (sessionType, options = {}) => {
    if (sessionType === 'paste_transform') {
      // Show paste UI first
      setStage('paste');
      return;
    }

    // For career coach and jd_match, start session directly
    setLoading(true);
    try {
      const response = await conversationService.startConversation({
        sessionType,
        interviewMode: options.interviewMode || 'quick'
      });

      if (response.success) {
        setSession(response.data);
        setStage('conversation');
      } else {
        toast.error(response.error || 'Failed to start conversation');
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast.error('Failed to start conversation');
    } finally {
      setLoading(false);
    }
  };

  const handlePasteSubmit = async (text) => {
    setLoading(true);
    try {
      const response = await conversationService.startConversation({
        sessionType: 'paste_transform',
        rawInput: text
      });

      if (response.success) {
        setSession(response.data);
        setStage('conversation');
        toast.success('Text analyzed successfully!');
      } else {
        toast.error(response.error || 'Failed to analyze text');
      }
    } catch (error) {
      console.error('Error parsing text:', error);
      toast.error('Failed to analyze text');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (message) => {
    if (!session) return;

    setLoading(true);
    try {
      const response = await conversationService.sendMessage(session.id, message);

      if (response.success) {
        setSession(response.data);
      } else {
        toast.error(response.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleCorrection = async (field, correctedValue) => {
    if (!session) return;

    setLoading(true);
    try {
      const response = await conversationService.makeCorrection(
        session.id,
        field,
        correctedValue
      );

      if (response.success) {
        // Update session with new extracted content
        setSession(prev => ({
          ...prev,
          extractedContent: response.data.extractedContent
        }));
        toast.success('Correction applied');
      } else {
        toast.error(response.error || 'Failed to apply correction');
      }
    } catch (error) {
      console.error('Error making correction:', error);
      toast.error('Failed to apply correction');
    } finally {
      setLoading(false);
    }
  };

  const handleFinalize = async () => {
    if (!session) return;

    setLoading(true);
    try {
      const response = await conversationService.finalizeConversation(session.id, {
        template: selectedTemplate
      });

      if (response.success) {
        toast.success('Resume created successfully!');
        // Navigate to the resume editor or resumes page
        navigate(`/resumes/${response.data.resume.id}`);
      } else {
        toast.error(response.error || 'Failed to create resume');
      }
    } catch (error) {
      console.error('Error finalizing:', error);
      toast.error('Failed to create resume');
    } finally {
      setLoading(false);
    }
  };

  const handleAbandon = async () => {
    if (!session) return;

    if (!window.confirm('Are you sure you want to abandon this conversation? Your progress will be lost.')) {
      return;
    }

    try {
      await conversationService.abandonConversation(session.id);
      setSession(null);
      setStage('select');
      toast.success('Conversation abandoned');
    } catch (error) {
      console.error('Error abandoning conversation:', error);
      toast.error('Failed to abandon conversation');
    }
  };

  const handleBack = () => {
    if (stage === 'paste') {
      setStage('select');
    } else if (stage === 'conversation' && session) {
      handleAbandon();
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loading size="lg" text="Loading..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {stage !== 'select' && (
              <button
                onClick={handleBack}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
            )}
            <div>
              <h1 className="text-xl font-bold text-gray-900">Resume Builder</h1>
              <p className="text-sm text-gray-500">
                {stage === 'select' && 'Choose how to build your resume'}
                {stage === 'paste' && 'Paste your experience'}
                {stage === 'conversation' && (session?.sessionType === 'career_coach' ? 'Career Coach Interview' : 'Building your resume')}
              </p>
            </div>
          </div>

          {stage === 'conversation' && session && (
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleAbandon}
              >
                Start Over
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleFinalize}
                disabled={loading || !session.extractedContent?.contactInfo?.fullName}
              >
                <CheckCircleIcon className="w-4 h-4 mr-1" />
                Finalize Resume
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {stage === 'select' && (
          <EntryPointSelector
            onSelect={handleEntryPointSelect}
            loading={loading}
          />
        )}

        {stage === 'paste' && (
          <PasteTransformFlow
            onSubmit={handlePasteSubmit}
            loading={loading}
          />
        )}

        {stage === 'conversation' && session && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Conversation panel */}
              <Card className="h-[calc(100vh-200px)] overflow-hidden flex flex-col">
                <ConversationUI
                  messages={session.messages || []}
                  onSendMessage={handleSendMessage}
                  onCorrect={handleCorrection}
                  loading={loading}
                  progress={session.sessionType === 'career_coach' ? {
                    current: session.currentQuestionIndex || 0,
                    total: session.interviewMode === 'deep' ? 12 : 8
                  } : null}
                  extractedContent={session.extractedContent}
                  disabled={session.status === 'completed'}
                />

                {/* Mobile preview button */}
                <div className="lg:hidden p-4 border-t border-gray-100">
                  <button
                    onClick={() => setMobilePreviewOpen(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <EyeIcon className="w-5 h-5" />
                    <span className="font-medium">Preview Resume</span>
                  </button>
                </div>
              </Card>

              {/* Preview panel - Desktop only */}
              <div className="hidden lg:block">
                <Card className="h-[calc(100vh-200px)] overflow-hidden flex flex-col">
                  {/* Template selector */}
                  <div className="p-4 border-b border-gray-100">
                    <p className="text-xs text-gray-500 mb-2">Choose template:</p>
                    <TemplateSelector
                      selectedTemplate={selectedTemplate}
                      onSelectTemplate={setSelectedTemplate}
                      isPro={isPro}
                    />
                  </div>

                  {/* Live preview */}
                  <div className="flex-1 overflow-hidden">
                    <ResumePreview
                      html={previewHtml}
                      loading={previewLoading}
                      hasWatermark={hasWatermark}
                      className="h-full"
                    />
                  </div>
                </Card>
              </div>
            </div>

            {/* Mobile preview drawer */}
            <MobilePreviewDrawer
              isOpen={mobilePreviewOpen}
              onClose={() => setMobilePreviewOpen(false)}
              html={previewHtml}
              loading={previewLoading}
              hasWatermark={hasWatermark}
              selectedTemplate={selectedTemplate}
              onSelectTemplate={setSelectedTemplate}
              isPro={isPro}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ResumeBuilderPage;
